import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * ProtectedRoute component
 * Wraps routes that require authentication
 * 
 * Features:
 * - Redirects unauthenticated users to internal login page
 * - Preserves intended destination for post-login redirect
 * - Optionally checks policy-specific access
 * - Shows loading state during auth check
 */
export default function ProtectedRoute({ 
  children, 
  requirePolicyAccess = false
}) {
  const { isAuthenticated, loading, hasAccessToPolicy } = useAuth();
  const location = useLocation();

  console.log('[ProtectedRoute] State:', { isAuthenticated, loading, pathname: location.pathname });

  // Show loading spinner while checking auth
  if (loading) {
    console.log('[ProtectedRoute] Loading, showing spinner');
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto" />
          <p className="mt-4 text-slate-600">Verifying your session...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to internal login
  if (!isAuthenticated) {
    console.log('[ProtectedRoute] Not authenticated, redirecting to login');
    const currentPath = location.pathname + location.search;
    const encodedReturnUrl = encodeURIComponent(currentPath);
    return <Navigate to={`/portal/login?returnUrl=${encodedReturnUrl}`} replace />;
  }

  // If policy-specific access is required, check it
  if (requirePolicyAccess) {
    // Extract policy number from URL params
    const pathParts = location.pathname.split('/');
    const policyIndex = pathParts.findIndex(part => 
      part === 'documents' || part === 'cancel'
    );
    
    if (policyIndex !== -1 && pathParts[policyIndex + 1]) {
      const policyNumber = pathParts[policyIndex + 1];
      
      if (!hasAccessToPolicy(policyNumber)) {
        // User doesn't have access to this policy
        return (
          <div className="min-h-screen bg-slate-50 flex items-center justify-center">
            <div className="text-center max-w-md mx-auto p-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🔒</span>
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
              <p className="text-slate-600 mb-6">
                You don't have access to this policy. Please ensure you're logged in with the correct account.
              </p>
              <a 
                href="/portal" 
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                Go to Dashboard
              </a>
            </div>
          </div>
        );
      }
    }
  }

  // Authenticated and authorized - render the protected content
  return children;
}
